import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { useQuery } from 'react-query'
import { LRUCache } from 'lru-cache'

import Sidebar from '../Sidebar'
import { useSidebar } from '../../context/SidebarContext'
import LoadBoardFilters from './LoadBoardFilters'
import Load from './Load'
import Spinner from './Spinner'
import LoggedOutModal from '../AuthPages/LoggedOutModal'
import SelectedFilters from './SelectedFilters'
import DeviceManagementModal from './DeviceManagementModal'
import Toast from './Searches/Toast'
import PostATruckModal from './PostATruckModal/PostATruckModal'
import UserDropdown from '../UserDropdown'
import AppLauncher from '../AppLauncher'
import SearchTabs from './SearchTabs'
import useSearchTabs from './useSearchTabs'

import './loadboard.css'

import {
	checkNewLoad,
	cleanupTitleBlinking,
	getOrderedLoads,
	isTitleBlinking,
	setBlinkRestoreSearchIcon,
} from './loadboardHelpers'
import { useDarkMode } from '../../context/DarkModeContext'
import { useSubscription } from '../../context/SubscriptionContext'
import { nearCity, fetchDefaultSounds } from '../../services/httpService'
import { getCurrentUser } from '../../services/authService'

import PaymentModal from './PaymentModal'
import Pagination from './Pagination'
import FacilityStatsModal from './FacilityStatsModal'

const BOARD_MODE = 'amazon'
// Mix mode lives in the MultiBoard app. Keep the default same-origin so the
// dev proxy and production ingress share localStorage/auth state.
const MIX_URL = process.env.REACT_APP_MIX_URL || '/multiboard'
const BOARD_OPTIONS = [
	{ value: 'amazon', label: 'Relay Mode' },
	{ value: 'mixed', label: 'Multi Mode', href: MIX_URL },
]

const initialFilters = {
	distance: '',
	driverType: '',
	maxdistance: '',
	dropLat: '',
	dropLng: '',
	dropCity: '',
	dropState: '',
	dropStateName: '',
	dropoffMaxDist: 100,
	pickupCity: '',
	pickupState: '',
	pickupStateName: '',
	pickupLat: '',
	pickupLng: '',
	pickupMaxDist: 50,
	equipType: '',
	pricePerMile: null,
	pricePerMileMin: '',
	price: '',
	payoutMin: '',
	sourceType: null,
	paginateSize: 1,
	startDateNearest: null,
	deadHeadShortest: null,
	loadsNewest: true,
	stopCount: 0,
	weight: 0,
	limited: false,
	ignoreLoadOut: false,
	lastSeen: 3,
	applySavedFilters: false,
	bobtail: false,
	isAIEnabled: false,
	loadType: '',
	workType: '',
	certRequired: '',
	origin: '',
	destination: '',
	sortBy: 'pickupDate',
	carrierGrades: [],

	priceThreshold: '',
	pickupLocations: [],
	dropoffLocations: [],
	destinationStates: [],
	destinationRegions: [],
	pickupDateFrom: '',
	deliveryDateTo: '',
}

const loadCache = new LRUCache({ max: 1000 })

export default function Loadboard() {
	const { isDarkMode } = useDarkMode()
	const { subscriptionData } = useSubscription()

	const itemsPerPage = 50

	const {
		sidebarWidth,
		isDrawerMode,
		viewMode,
		setViewMode,
		sidebarIconMode,
		setSidebarIconMode,
	} = useSidebar()
	const [isAudioOn, setAudioOn] = useState(() => {
		const saved = localStorage.getItem('loadfetcher-audio-on')
		return saved === null ? false : JSON.parse(saved)
	})
	const [paymentRequired, setPaymentRequired] = useState(false)
	const [invoiceLoading, setInvoiceLoading] = useState(false)
	const [showPaymentModal, setShowPaymentModal] = useState(false)
	const [paymentModalBillingCycle, setPaymentModalBillingCycle] =
		useState('yearly')
	const [isAutoRefresh, setIsAutoRefresh] = useState(false)
	const currentUser = getCurrentUser()
	const [refreshInterval, setRefreshInterval] = useState(() => {
		const saved = localStorage.getItem('loadfetcher-refresh-interval')
		return saved === null ? 10 : parseInt(saved, 10)
	})
	const [continueOnAlert, setContinueOnAlert] = useState(() => {
		const saved = localStorage.getItem('loadfetcher-continue-on-alert')
		return saved === null ? false : JSON.parse(saved)
	})
	const [showDeviceModal, setShowDeviceModal] = useState(false)
	const [showToast, setShowToast] = useState(false)
	const [toastMessage, setToastMessage] = useState('')
	const [toastError, setToastError] = useState(false)
	const [isPostTruckModalOpen, setIsPostTruckModalOpen] = useState(false)
	const [selectedLoad, setSelectedLoad] = useState(null)
	const [layout, setLayout] = useState(() => {
		if (typeof window === 'undefined') return 'desktop'
		if (window.matchMedia('(max-width: 767px)').matches) return 'mobile'
		if (window.matchMedia('(max-width: 1023px)').matches) return 'tablet'
		return 'desktop'
	})

	useEffect(() => {
		if (typeof window === 'undefined') return
		const mobileQuery = window.matchMedia('(max-width: 767px)')
		const tabletQuery = window.matchMedia('(max-width: 1023px)')
		const syncLayout = () => {
			if (mobileQuery.matches) setLayout('mobile')
			else if (tabletQuery.matches) setLayout('tablet')
			else setLayout('desktop')
		}
		if (mobileQuery.addEventListener) {
			mobileQuery.addEventListener('change', syncLayout)
			tabletQuery.addEventListener('change', syncLayout)
			return () => {
				mobileQuery.removeEventListener('change', syncLayout)
				tabletQuery.removeEventListener('change', syncLayout)
			}
		}
		// Safari < 14 fallback
		mobileQuery.addListener(syncLayout)
		tabletQuery.addListener(syncLayout)
		return () => {
			mobileQuery.removeListener(syncLayout)
			tabletQuery.removeListener(syncLayout)
		}
	}, [])

	const isMobile = layout === 'mobile'
	const isTablet = layout === 'tablet'
	const [moveResultsToTop, setMoveResultsToTop] = useState(() => {
		const saved = localStorage.getItem('loadfetcher-move-results-to-top')
		return saved === null ? false : JSON.parse(saved)
	})
	const [showQuickPost, setShowQuickPost] = useState(() => {
		const saved = localStorage.getItem('loadfetcher-show-quick-post')
		return saved === null ? false : JSON.parse(saved)
	})
	const [contentWidth, setContentWidth] = useState(() => {
		const saved = localStorage.getItem('loadfetcher-content-width')
		// 'focused' is the legacy name for the narrow width
		return saved === 'wide' ? 'wide' : 'narrow'
	})
	const [facilityModalCode, setFacilityModalCode] = useState(null)
	const handleFacilityClick = useCallback(code => {
		if (code && code.length <= 4) setFacilityModalCode(code)
	}, [])
	const showSearchTabs = viewMode !== 'focused'
	// Separate refresh tracking that doesn't cause re-renders
	const refreshTrackingRef = useRef({
		lastSource: 'initial', // 'initial' | 'auto' | 'button' | 'filter'
		timestamp: Date.now(),
	})

	// Track auto-refresh start time for 8-hour limit
	const autoRefreshStartRef = useRef(null)

	// Bot detection tracking
	const [buttonClickTracker, setButtonClickTracker] = useState({
		clickTimes: [], // Array of timestamps for consecutive rapid clicks
		consecutiveRapidClicks: 0, // Count of consecutive clicks < 8 seconds apart
		botDetected: false, // Flag for programmatic clicking detection
	})

	const seedFilters = useMemo(
		() => ({
			...initialFilters,
			priceThreshold: showSearchTabs
				? localStorage.getItem('loadfetcher-price-threshold') || ''
				: '',
		}),
		[showSearchTabs],
	)

	const {
		tabs,
		activeTab,
		activeTabId,
		addTab,
		closeTab,
		setActiveTab,
		updateActiveTabFilters,
		error: tabsError,
	} = useSearchTabs(seedFilters, BOARD_MODE, { persist: showSearchTabs })

	const filters = activeTab.filters
	const setFilters = updateActiveTabFilters
	const currentPage = Math.max(1, Number(filters.paginateSize) || 1)

	const prepareFiltersForAutoRefresh = useCallback(
		nextFilters => {
			if (!isAutoRefresh || !nextFilters?.limited) return nextFilters
			return {
				...nextFilters,
				limited: false,
				sortBy: 'pickupDate',
				paginateSize: 1,
			}
		},
		[isAutoRefresh],
	)

	const handleSelectSearchTab = useCallback(
		tabId => {
			setActiveTab(tabId, prepareFiltersForAutoRefresh)
		},
		[prepareFiltersForAutoRefresh, setActiveTab],
	)

	useEffect(() => {
		if (!tabsError) return
		setToastMessage(tabsError)
		setToastError(true)
		setShowToast(true)
		const timer = setTimeout(() => setShowToast(false), 3000)
		
		return () => {
			clearTimeout(timer)
			setShowToast(false)
		}
	}, [tabsError])

	const { data, isLoading, isError, error, refetch, isFetching } = useQuery(
		['loads', { ...filters, page: currentPage, itemsPerPage }],
		() =>
			nearCity({
				...filters,
				number: currentPage,
				size: itemsPerPage,
				setPaymentRequired,

				// Pass multi-city arrays from filters
				pickupLocations:
					filters.pickupLocations.length > 0
						? filters.pickupLocations
						: undefined,
				dropoffLocations:
					filters.dropoffLocations.length > 0
						? filters.dropoffLocations
						: undefined,

				// Cryptic bot detection field
				...(buttonClickTracker.botDetected && { _mc: 1 }),
			}),
		{
			refetchOnWindowFocus: false,
			onSuccess: data => {
				if (!data) return
				const numericPriceThreshold = parseFloat(filters.priceThreshold) || 0
				const refreshSource = refreshTrackingRef.current.lastSource
				const { loads: processedLoads, isNewLoadDetected } = checkNewLoad(
					data,
					loadCache,
					isAudioOn,
					numericPriceThreshold,
					refreshSource,
					continueOnAlert
				)
				data.loads = processedLoads

				if (isNewLoadDetected && isAutoRefresh && !continueOnAlert) setIsAutoRefresh(false)
			},
			retry: (failureCount, error) => {
				// Don't retry on 429 (Too Many Requests) or 401 (Unauthorized)
				if (
					error?.response?.status === 429 ||
					error?.response?.status === 401
				) {
					return false
				}
				// Default React Query retry behavior for other errors (3 retries)
				return failureCount < 2
			},
		}
	)
	const orderedLoads = getOrderedLoads(data?.loads, moveResultsToTop)

	useEffect(() => {
		fetchDefaultSounds()
	}, [])

	useEffect(() => {
		if (!showSearchTabs) return
		if (filters.priceThreshold !== undefined) {
			localStorage.setItem('loadfetcher-price-threshold', filters.priceThreshold)
		}
	}, [filters.priceThreshold, showSearchTabs])

	useEffect(() => {
		if (isError) {
			let errorMessage = ''

			if (typeof error === 'string') {
				errorMessage = error
			} else if (error instanceof Error) {
				errorMessage = error.message
			} else if (error?.response?.status) {
				errorMessage = `${error.response.status}`
			} else if (error?.message) {
				errorMessage = error.message
			} else {
				errorMessage = JSON.stringify(error)
			}

			console.log('Error message:', errorMessage)

			if (errorMessage.includes('429')) {
				// Show toast for too many requests
				setToastMessage('Too many requests being sent. Slow down!')
				setToastError(true)
				setShowToast(true)

				// Hide toast after 5 seconds
				setTimeout(() => {
					setShowToast(false)
				}, 5000)
			} else if (errorMessage.includes('401')) {
				setIsAutoRefresh(false)
			} else if (errorMessage.includes('402')) {
				// Payment required - show modal only for non-Pro users
				setIsAutoRefresh(false)
				setPaymentRequired(true)
				// Only show modal if user doesn't have Pro access
				const user = getCurrentUser()
				if (!user?.subscription?.type?.includes('pro')) {
					// Default to yearly unless user's current plan is monthly
					const isCurrentlyMonthly =
						user?.subscription?.type?.includes('monthly')
					const billingCycle = isCurrentlyMonthly ? 'monthly' : 'yearly'

					openPaymentModal(billingCycle)
				}
			} else if (errorMessage.includes('403')) {
				setShowDeviceModal(true)
			}
		}
	}, [isError, error])

	const handlePrevPage = () => {
		setFilters(prevFilters => ({
			...prevFilters,
			paginateSize: Math.max(1, (Number(prevFilters.paginateSize) || 1) - 1),
		}))
		scrollToTop()
	}

	const handleNextPage = () => {
		setFilters(prevFilters => ({
			...prevFilters,
			paginateSize: (Number(prevFilters.paginateSize) || 1) + 1,
		}))
		scrollToTop()
	}

	const scrollToTop = () => {
		window.scrollTo({ top: 0, behavior: 'smooth' })
	}

	const resetFilters = () => {
		setFilters(initialFilters)
	}

	const removeFilter = (key, value) => {
		if (key === 'pickupLocations' || key === 'dropoffLocations') {
			setFilters(prevFilters => ({
				...prevFilters,
				[key]: prevFilters[key].filter(location => location.name !== value),
				paginateSize: 1,
			}))
		} else if (key === 'destinationStates') {
			// Handle state array filters - remove all if value is 'all', otherwise remove specific
			if (value === 'all') {
				setFilters(prevFilters => ({
					...prevFilters,
					[key]: [],
					paginateSize: 1,
				}))
			} else {
				setFilters(prevFilters => ({
					...prevFilters,
					[key]: prevFilters[key].filter(item => item !== value),
					paginateSize: 1,
				}))
			}
		} else if (key === 'destinationRegions') {
			if (value === 'all') {
				setFilters(prevFilters => ({
					...prevFilters,
					[key]: [],
					paginateSize: 1,
				}))
			} else {
				setFilters(prevFilters => ({
					...prevFilters,
					[key]: prevFilters[key].filter(item => item !== value),
					paginateSize: 1,
				}))
			}
		} else if (key === 'sourceType') {
			// Handle source array filters - remove by index
			setFilters(prevFilters => ({
				...prevFilters,
				[key]: prevFilters[key].filter((_, index) => index !== value),
				paginateSize: 1,
			}))
		} else {
			// Handle regular filters - reset to initial value
			setFilters(prevFilters => ({
				...prevFilters,
				[key]: initialFilters[key],
				paginateSize: 1,
			}))
		}
	}

	const openPostTruckModal = useCallback(load => {
		setSelectedLoad(load)
		setIsPostTruckModalOpen(true)
	}, [])

	// Helper functions to open payment modal with specific billing cycles
	const openPaymentModal = (billingCycle = 'yearly') => {
		setPaymentModalBillingCycle(billingCycle)
		setShowPaymentModal(true)
	}

	const closePostTruckModal = useCallback(() => {
		setIsPostTruckModalOpen(false)
		setSelectedLoad(null)
	}, [])

	useEffect(() => {
		// Check if this was triggered within 100ms of a programmatic refresh
		const timeSinceLastProgrammaticRefresh =
			Date.now() - refreshTrackingRef.current.timestamp
		const wasProgrammaticRefresh = timeSinceLastProgrammaticRefresh < 100

		if (!wasProgrammaticRefresh) {
			refreshTrackingRef.current = {
				lastSource: 'filter',
				timestamp: Date.now(),
			}
		}

		refetch()
		setPaymentRequired(false)
	}, [filters, currentPage])

	useEffect(() => {
		if (window.$crisp) {
			window.$crisp.push(['do', 'chat:hide'])
		}

		return () => {
			if (window.$crisp) {
				window.$crisp.push(['do', 'chat:show'])
			}
			cleanupTitleBlinking()
		}
	}, [])

	// Auto-refresh 8-hour limit
	useEffect(() => {
		if (isAutoRefresh) {
			if (!autoRefreshStartRef.current) {
				autoRefreshStartRef.current = Date.now()
			}

			const checkInterval = setInterval(() => {
				const elapsed = Date.now() - autoRefreshStartRef.current
				if (elapsed >= 8 * 60 * 60 * 1000) {
					setIsAutoRefresh(false)
					autoRefreshStartRef.current = null
				}
			}, 60000)

			return () => clearInterval(checkInterval)
		} else {
			autoRefreshStartRef.current = null
		}
	}, [isAutoRefresh])

	useEffect(() => {
		const baseTitle = ' LoadFetcher'
		if (isTitleBlinking()) {
			setBlinkRestoreSearchIcon(isAutoRefresh)
			return
		}
		document.title = isAutoRefresh ? `🔎 ${baseTitle}` : baseTitle
	}, [isAutoRefresh])

	const handleManualRefresh = () => {
		refreshTrackingRef.current = { lastSource: 'button', timestamp: Date.now() }
		return refetch()
	}

	const handleAutoRefresh = () => {
		refreshTrackingRef.current = { lastSource: 'auto', timestamp: Date.now() }
		return refetch()
	}

	const handleSetAutoRefresh = value => {
		setIsAutoRefresh(value)
		if (value && filters.limited) {
			setFilters(prev => ({
				...prev,
				limited: false,
				sortBy: 'pickupDate',
				paginateSize: 1,
			}))
		}
	}

	const handleSetContinueOnAlert = value => {
		setContinueOnAlert(value)
		localStorage.setItem('loadfetcher-continue-on-alert', JSON.stringify(value))
	}

	const handleSetShowQuickPost = value => {
		setShowQuickPost(value)
		localStorage.setItem('loadfetcher-show-quick-post', JSON.stringify(value))
	}

	const handleSetMoveResultsToTop = value => {
		setMoveResultsToTop(value)
		localStorage.setItem('loadfetcher-move-results-to-top', JSON.stringify(value))
	}

	const handleSetAudioOn = value => {
		setAudioOn(value)
		localStorage.setItem('loadfetcher-audio-on', JSON.stringify(value))
	}

	const handleSetContentWidth = value => {
		setContentWidth(value)
		localStorage.setItem('loadfetcher-content-width', value)
	}

	const handleSetRefreshInterval = value => {
		setRefreshInterval(value)
		localStorage.setItem('loadfetcher-refresh-interval', value.toString())
	}

	// Function to detect bot/programmatic clicking
	const handleButtonClick = () => {
		const now = Date.now()

		setButtonClickTracker(prev => {
			const lastClickTime =
				prev.clickTimes.length > 0
					? prev.clickTimes[prev.clickTimes.length - 1]
					: null

			let newConsecutiveCount = prev.consecutiveRapidClicks

			if (lastClickTime && now - lastClickTime < 8000) {
				// Less than 8 seconds since last click - increment counter
				newConsecutiveCount++
			} else {
				// More than 8 seconds or first click - reset counter
				newConsecutiveCount = lastClickTime ? 0 : 0 // Reset if gap > 8s
			}

			// Add current click time
			const newClickTimes = [...prev.clickTimes, now]

			// Keep only last 20 clicks for reference
			if (newClickTimes.length > 30) {
				newClickTimes.shift()
			}

			// Check if we reached 20 consecutive rapid clicks (bot behavior detected)
			if (newConsecutiveCount >= 25) {
				return {
					clickTimes: newClickTimes,
					consecutiveRapidClicks: newConsecutiveCount,
					botDetected: true,
				}
			}

			return {
				clickTimes: newClickTimes,
				consecutiveRapidClicks: newConsecutiveCount,
				botDetected: prev.botDetected, // Keep existing bot detection status
			}
		})
	}

	const user = getCurrentUser()

	useEffect(() => {
		const checkPaymentStatus = () => {
			try {
				const clientEmail = user?.email
				const location = window.location.pathname.includes('/loadboard')

				if (!clientEmail || !location) return

				if (!subscriptionData) {
					setInvoiceLoading(true)
					return
				}

				setInvoiceLoading(false)

				const dateFromServer = new Date(subscriptionData?.trialEndTime)
				const currentDate = new Date()
				const isDateInPast = dateFromServer < currentDate

				if (subscriptionData?.payed) {
					setPaymentRequired(false)
					if (paymentRequired) refetch()
					return
				}

				if (!isDateInPast) {
					setPaymentRequired(false)
					if (paymentRequired) refetch()
					return
				}
			} catch (error) {
				console.log('error', error)
				setInvoiceLoading(false)
			}
		}

		checkPaymentStatus()
	}, [subscriptionData])

	const contentMaxWidthClass =
		contentWidth === 'narrow' ? 'max-w-screen-xl' : 'max-w-[1640px]'
	
	const contentMaxWidthPx = contentWidth === 'narrow' ? '1280px' : '1640px'
	const burgerClearanceClass = isDrawerMode ? 'lb-burger-clearance' : ''

	return (
		<>
			<div
				className={burgerClearanceClass}
				style={{
					backgroundColor: isDarkMode ? '#1c2127' : '#ffffff',
					'--lb-content-max-w': contentMaxWidthPx,
				}}
			>
				<Sidebar />
				<div
					style={{
						paddingLeft: `${sidebarWidth}px`,
						transition: 'padding-left 300ms ease-in-out',
					}}
				>
				<div
					className={`lb-pad-x top-0 z-40 mx-auto flex h-16 w-full ${contentMaxWidthClass} shrink-0 items-center gap-x-4 pl-14 xl:pl-8 pr-4 sm:pr-6 lg:pr-8`}
					style={{ backgroundColor: isDarkMode ? '#1c2127' : '#ffffff' }}
				>
					<SearchTabs
						tabs={tabs}
						activeTabId={activeTabId}
						onSelect={handleSelectSearchTab}
						onClose={closeTab}
						onAdd={addTab}
						boardMode={BOARD_MODE}
						boardOptions={BOARD_OPTIONS}
						showBoardModeTabs={true}
						hideSearchTabs={!showSearchTabs}
						isDarkMode={isDarkMode}
					/>
					<div className="ml-auto flex items-center gap-x-3 shrink-0">
						<AppLauncher />
						<UserDropdown />
					</div>
				</div>

				<main className={`min-h-screen overflow-hidden rounded-tl-lg ${isDarkMode ? 'bg-dark-color' : 'bg-white'}`}>
					<div className={`mx-auto w-full ${contentMaxWidthClass}`}>
					<LoadBoardFilters
						isDarkMode={isDarkMode}
						refetch={refetch}
						handleManualRefresh={handleManualRefresh}
						handleAutoRefresh={handleAutoRefresh}
						handleButtonClick={handleButtonClick}
						botDetected={buttonClickTracker.botDetected}
						filters={filters}
						setFilters={setFilters}
						resetFilters={resetFilters}
						isAudioOn={isAudioOn}
						setAudioOn={handleSetAudioOn}
						setIsAutoRefresh={handleSetAutoRefresh}
						isAutoRefresh={isAutoRefresh}
						moveResultsToTop={moveResultsToTop}
						setMoveResultsToTop={handleSetMoveResultsToTop}
						refreshInterval={refreshInterval}
						setRefreshInterval={handleSetRefreshInterval}
						isFetching={isFetching}
						userType={currentUser?.userType}
						isAdmin={currentUser?.isAdmin}
						continueOnAlert={continueOnAlert}
						setContinueOnAlert={handleSetContinueOnAlert}
						showQuickPost={showQuickPost}
						setShowQuickPost={handleSetShowQuickPost}
						contentWidth={contentWidth}
						setContentWidth={handleSetContentWidth}
						viewMode={viewMode}
						setViewMode={setViewMode}
						sidebarIconMode={sidebarIconMode}
						setSidebarIconMode={setSidebarIconMode}
					/>

					<div
						className={`${
							isDarkMode ? 'bg-dark-color' : 'bg-white'
						} lb-pad-x w-full px-4 py-2 sm:px-6 lg:px-8`}
					>
						<div className="flex md:hidden flex-grow">
							<SelectedFilters
								filters={filters}
								initialFilters={initialFilters}
								removeFilter={removeFilter}
								clearAllFilters={resetFilters}
							/>
						</div>
						<div className="hidden md:flex min-h-[24px] justify-start items-center gap-x-1">
							<div className="flex flex-grow">
								<SelectedFilters
									filters={filters}
									initialFilters={initialFilters}
									removeFilter={removeFilter}
									clearAllFilters={resetFilters}
								/>
							</div>
						</div>
					</div>

					<div className="lb-pad-x mb-5 px-4 sm:px-6 lg:px-8" style={{ minHeight: '53vh' }}>
						{isLoading || isFetching || invoiceLoading ? (
							<Spinner isDarkMode={isDarkMode} />
						) : isError && error?.response?.status === 429 ? (
							<div
								className={`message-container ${
									isDarkMode ? 'text-white' : 'text-black'
								} pt-8 pb-8 flex justify-center items-center text-2xl font-medium`}
							>
								Something went wrong, please try again later.
							</div>
						) : Array.isArray(orderedLoads) && orderedLoads?.length ? (
							orderedLoads?.map(load => (
								<Load
									key={load?._id}
									load={load}
									originLat={
										filters.pickupLocations?.length > 0
											? filters.pickupLocations[0].lat
											: filters.pickupLat
									}
									originLng={
										filters.pickupLocations?.length > 0
											? filters.pickupLocations[0].lng
											: filters.pickupLng
									}
									onPostTruck={openPostTruckModal}
									showQuickPost={showQuickPost}
									onFacilityClick={handleFacilityClick}
									isMobile={isMobile}
									isTablet={isTablet}
								/>
							))
							) : (
								<div
									className={`message-container h-[48vh] md:h-[60vh] ${
										isDarkMode ? 'text-white' : 'text-black'
									} pt-8 pb-8 flex justify-center items-center text-2xl font-medium`}
								>
									<span
										style={{
											transform: `translateX(-${sidebarWidth / 2}px)`,
										}}
									>
										No matching data
									</span>
								</div>
							)}
					</div>

					{/* Pagination controls */}
					{!isLoading &&
						!isFetching &&
						!invoiceLoading &&
						!(isError && error?.response?.status === 429) &&
						((Array.isArray(orderedLoads) &&
							orderedLoads?.length > 0 &&
							data?.totalResults > itemsPerPage) ||
							currentPage > 1) && (
							<div
								className="flex justify-center"
								style={{ backgroundColor: isDarkMode ? '#1c2128' : '#ffffff' }}
							>
								<Pagination
									onPrevious={handlePrevPage}
									onNext={handleNextPage}
									currentPage={currentPage}
									isPreviousDisabled={currentPage === 1}
									isNextDisabled={
										currentPage * itemsPerPage >=
											(data?.totalResults || 0) ||
										!orderedLoads?.length ||
										orderedLoads?.length < 50
									}
									isDarkMode={isDarkMode}
								/>
							</div>
						)}
					</div>
				</main>
				</div>
			</div>

			<LoggedOutModal />

			<DeviceManagementModal
				isOpen={showDeviceModal}
				onClose={() => setShowDeviceModal(false)}
				refetch={refetch}
				isDarkMode={isDarkMode}
			/>

			{showToast && (
				<Toast
					message={toastMessage}
					error={toastError}
					onClose={() => setShowToast(false)}
				/>
			)}

			<PostATruckModal
				isOpen={isPostTruckModalOpen}
				onClose={closePostTruckModal}
				load={selectedLoad}
				isDarkMode={isDarkMode}
			/>

			<PaymentModal
				isOpen={showPaymentModal}
				onClose={() => setShowPaymentModal(false)}
				isDarkMode={isDarkMode}
				initialBillingCycle={paymentModalBillingCycle}
			/>

			<FacilityStatsModal
				facilityCode={facilityModalCode}
				isOpen={!!facilityModalCode}
				onClose={() => setFacilityModalCode(null)}
			/>
		</>
	)
}
